// /Users/donovan68/dva-frontend-vite/src/layouts/AppShell.jsx
// ============================================================
// UPDATED: Uses useDemoPlan hook for demo mode support
// UPDATED: Added hideSecondaryNav prop to hide secondary nav for EA routes
// UPDATED: EA routes get simplified header without Home/Tools/Documents/Messages
// ============================================================

import React from 'react';
import { useLocation } from 'react-router-dom';
import AppHeader from '../components/header/AppHeader';
import { useClientProfile } from '../hooks/useClientProfile';
import { useFilter } from '../contexts/FilterContext';
import { usePageContext } from '../contexts/PageContext';
import { useDemoPlan } from '../hooks/useDemoPlan';
import { useHelp } from '../help/useHelp';
// Enterprise Version prototype (Phase 2 shell) — additive, gated by
// VITE_ENABLE_ENTERPRISE_PROTOTYPE. The mode toggle lives in the profile dropdown; the
// navy sub-nav renders only on /enterprise routes. Nothing renders when the flag is off.
import { EnterpriseModeToggle, EnterpriseSubNav, EnterpriseJourneyBar, useEnterpriseMode } from '../prototype/enterprise';

export default function AppShell({ children, hideSecondaryNav = false }) {
  const location = useLocation();

  // Shared filters for tabs
  const { activeFilter, setActiveFilter } = useFilter();

  // Page context set by pages (headline, title, breadcrumbs, tabs)
  const { page } = usePageContext();

  // Client profile for user data (organization name, etc.)
  const { data, isLoading, error } = useClientProfile();

  // Get plan from demo hook (respects demo mode override)
  const { plan } = useDemoPlan();

  // In-app help overlay; the header entry point renders on EA routes only (Phase 1).
  const { openHelp, isOpen: helpOpen } = useHelp();

  // Check if we're on an EA route. EA is now the promoted application; these
  // are its de-prefixed routes, plus legacy /ea/* (redirected) for back-compat.
  const EA_ROUTE_PREFIXES = [
    '/home', '/opportunities', '/activate', '/weights', '/upgrade',
    '/opportunity/', '/execute/', '/strategy-brief', '/personalization-variant',
  ];
  const isEARoute = location.pathname.startsWith('/ea')
    || EA_ROUTE_PREFIXES.some((p) => location.pathname === p || location.pathname.startsWith(p));

  // D4: the EA experience greets the logged-in person (Dana Whitfield,
  // VP of Analytics); non-EA surfaces keep the organization name.
  // Covers EA routes and EA focus-mode pages (fromEA).
  const baseName = data?.organization_name || 'User';
  // Enterprise prototype routes are the same Client Admin (Dana) as the EA experience.
  const isEnterpriseRoute = location.pathname.startsWith('/enterprise');
  const enterprisePrototypeEnabled = import.meta.env.VITE_ENABLE_ENTERPRISE_PROTOTYPE === 'true';
  // Enterprise mode is STICKY STATE (the dropdown toggle), NOT the route — so navigating via
  // the journey bar to storefront routes keeps the navy bar present. Suppresses storefront
  // chrome (search/upgrade) and shows the navy sub-nav on every route while active.
  const { isEnterprise: enterpriseModeActive } = useEnterpriseMode();
  // Detect EA focus-mode from BOTH router state and the query string — matching
  // EAFocusRouteWrapper.useIsEAFocusMode. The home navigates to shared pages (e.g. /client-profile)
  // with fromEA in router STATE, so checking only location.search greeted the person as "User".
  const isEA = isEARoute || isEnterpriseRoute || enterpriseModeActive
    || location.state?.fromEA === true || location.search.includes('fromEA');
  const user = {
    name: isEA ? 'Dana Whitfield' : baseName,
    avatarUrl: undefined
  };

  const handleSearch = (query) => {
    console.log('Search query:', query);
  };

  const handleTabChange = (tab) => {
    console.log('🔑 Tab clicked:', tab);
    console.log('🔑 Current activeFilter:', activeFilter);
    
    if (tab === 'Tools' || tab === 'Documents' || tab === 'Messages') {
      setActiveFilter(tab);
      console.log('🔑 Set activeFilter to:', tab);
    } else {
      setActiveFilter('Tools');
      console.log('🔑 Set activeFilter to: Tools (default)');
    }
  };

  // Route fallback when a page hasn't set PageContext yet
  const routeFallback = (() => {
    const path = location.pathname;
    
    // EA routes - always return empty breadcrumbs
    if (isEARoute) {
      return {
        title: 'EA Prototype',
        breadcrumbs: []
      };
    }
    
    // Freemium routes
    if (path.includes('/freemium/personalize')) {
      return { 
        title: 'Personalize Your Experience',
        breadcrumbs: [
          { label: 'Home', href: '/home' },
          { label: 'Personalize' }
        ]
      };
    }
    if (path.includes('/freemium/strategy')) {
      return { 
        title: 'View Industry Insights',
        breadcrumbs: [
          { label: 'Home', href: '/home' },
          { label: 'Industry Insights' }
        ]
      };
    }
    if (path.includes('/freemium/upgrade')) {
      return { 
        title: 'Upgrade to DVA Pro',
        breadcrumbs: [
          { label: 'Home', href: '/home' },
          { label: 'Upgrade' }
        ]
      };
    }
    
    // Pro Journey routes - return empty breadcrumbs to hide the white band
    if (path === '/home' || path === '/') {
      return { 
        title: 'View Industry Insights',
        breadcrumbs: []
      };
    }
    if (path === '/pro/analysis' || path.startsWith('/pro/analysis/')) {
      return {
        title: 'Analysis & Qualification',
        breadcrumbs: []
      };
    }
    if (path === '/pro/roadmap-planning') {
      return {
        title: 'Roadmap Planning',
        breadcrumbs: []
      };
    }
    if (path === '/pro/approval') {
      return {
        title: 'Approval & Governance',
        breadcrumbs: []
      };
    }
    if (path === '/pro/execution') {
      return {
        title: 'Execution & Tracking',
        breadcrumbs: []
      };
    }
    
    // Other Pro routes
    if (path.includes('/pro/personalize') || path === '/personalize') {
      return { 
        title: 'Personalization',
        breadcrumbs: [
          { label: 'Home', href: '/home' },
          { label: 'Personalize' }
        ]
      };
    }
    if (path.includes('/pro/roi')) {
      return { 
        title: 'ROI Modeling',
        breadcrumbs: [
          { label: 'Home', href: '/home' },
          { label: 'ROI' }
        ]
      };
    }
    if (path.includes('/pro/roadmap') && path !== '/pro/roadmap-planning') {
      return { 
        title: 'Implementation Roadmap',
        breadcrumbs: [
          { label: 'Home', href: '/home' },
          { label: 'Roadmap' }
        ]
      };
    }
    
    // Common routes
    if (path.includes('/matrix')) {
      return { 
        title: 'Use Case Matrix',
        breadcrumbs: [
          { label: 'Home', href: '/home' },
          { label: 'Matrix' }
        ]
      };
    }
    if (path.includes('/results')) {
      return { 
        title: 'Results & Analysis',
        breadcrumbs: [
          { label: 'Home', href: '/home' },
          { label: 'Results' }
        ]
      };
    }
    if (path.includes('/settings')) {
      return { 
        title: 'Settings',
        breadcrumbs: [
          { label: 'Home', href: '/home' },
          { label: 'Settings' }
        ]
      };
    }
    
    return { 
      title: 'View Industry Insights',
      breadcrumbs: []
    };
  })();

  const headline = page.headline || '';
  const contextTitle = page.title || routeFallback.title;
  
  let breadcrumbs;
  if (page.breadcrumbs !== undefined) {
    breadcrumbs = page.breadcrumbs;
  } else {
    breadcrumbs = routeFallback.breadcrumbs;
  }
  
  // Force empty breadcrumbs for Journey routes AND EA routes
  if (isEARoute || hideSecondaryNav) {
    breadcrumbs = [];
  }
  
  // For EA routes or when hideSecondaryNav is true, don't show tabs
  const shouldHideSecondaryNav = hideSecondaryNav || isEARoute;
  
  // Tabs (only show if not hiding secondary nav)
  const tabs = shouldHideSecondaryNav
    ? []
    : (page.rightSlot ? [] : (page.tabs?.length ? page.tabs : ['Tools', 'Documents', 'Messages']));
  
  const activeTab = activeFilter || 'Tools';
  
  console.log('🔵 AppShell - activeFilter:', activeFilter, 'activeTab:', activeTab, 'shouldHideSecondaryNav:', shouldHideSecondaryNav);

  // Message count
  const messageCount = plan === 'pro' ? 4 : 0;

  if (error) {
    console.error('Error loading client profile:', error);
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-100/80 via-blue-50/40 to-slate-50">
      <AppHeader 
        plan={plan} 
        user={user} 
        workspaceTitle="Data Value Accelerator"
        onSearch={handleSearch}
        variant="glass"
        headline={headline}
        contextTitle={contextTitle}
        breadcrumbs={breadcrumbs}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        messageCount={messageCount}
        rightSlot={page.rightSlot}
        // For EA routes, use EA-specific upgrade path
        upgradeHref={isEARoute ? '/upgrade' : '/freemium/upgrade'}
        // Enterprise mode-flip: the Standard⟷Enterprise pill lives in the profile dropdown
        // (consumes no header row); in Enterprise mode the search + upgrade chrome is hidden.
        // Both additive and flag-gated — absent when the flag is off (storefront unchanged).
        enterpriseMode={enterpriseModeActive}
        userMenuExtra={enterprisePrototypeEnabled ? <EnterpriseModeToggle /> : undefined}
        onHelpClick={isEARoute ? () => openHelp() : undefined}
        helpOpen={helpOpen}
      />

      {/* Navy enterprise sub-nav — ON TOP and PERSISTENT while Enterprise mode is active
          (sticky state), across every route including storefront steps reached via the
          journey bar. Its absence in Standard is the before/after delta. */}
      {enterpriseModeActive && <EnterpriseSubNav />}

      {/* Journey bar, below the navy bar. On /enterprise stub routes (no EA page) AppShell
          renders it; on storefront routes the EA page renders its own, so we don't double it. */}
      {enterpriseModeActive && isEnterpriseRoute && <EnterpriseJourneyBar />}

      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <div className="text-sm text-slate-600">Loading…</div>
        </div>
      ) : (
        <div>
          {children}
        </div>
      )}
    </div>
  );
}